import { BaseModal } from './BaseModal'
import { getToday, DAILY_LIST, MINI_DAILY_LIST } from '../../lib/words'
import { solvesMap } from '../../lib/stats'
import { Puzzle } from '../../components/puzzle/Puzzle'
import { Link } from 'react-router-dom'
import { ButtonToggle } from '../../components/modals/ButtonToggle'
import DatePicker from 'react-datepicker'
import { useState, forwardRef, useMemo } from 'react'
import { CalendarIcon } from '@heroicons/react/outline'
import { FixedSizeList as List } from 'react-window';
import { useRef, useEffect, useCallback } from 'react';
import { User } from '../../lib/user'
import { GAME_MODE_DAILY, GAME_MODE_MINI_DAILY, GameMode } from '../../constants/strings'
import BullseyeImage from '../../imgs/scores/bullseye.png'
import TwoImage from '../../imgs/scores/2.svg'
import ThreeImage from '../../imgs/scores/3.svg'
import FourImage from '../../imgs/scores/4.svg'
import FiveImage from '../../imgs/scores/5.svg'
import SixImage from '../../imgs/scores/6.svg'
import SadImage from '../../imgs/scores/sad.svg'
import { useMediaQuery } from 'react-responsive'

import "react-datepicker/dist/react-datepicker.css"

type Props = {
  mode: GameMode,
  index: number|null,
  user: User,
  isOpen: boolean,
  isDarkMode: boolean,
  handleClose: () => void
}

const numToImage = (num: number) => {
  switch(num) {
    case 1:
      return BullseyeImage
    case 2:
      return TwoImage
    case 3:
      return ThreeImage
    case 4:
      return FourImage
    case 5:
      return FiveImage
    case 6:
      return SixImage
    default:
      return SadImage
  }
}

const getYesterday = () => {
  const yesterday = new Date(getToday())
  yesterday.setDate(yesterday.getDate() - 1)
  return yesterday
}

// TODO start date based on current puzzle index + mode
// if current puzzle is unlimited or otherwise
export const ArchiveModal = ({ user, mode, index, isOpen, isDarkMode, handleClose}: Props) => {

  const stats = user.stats()
  const isSm = useMediaQuery({ query: '(min-width: 640px)' })
  const [archiveMode, setArchiveMode] = useState(mode)
  const listRef = useRef<any>(null);
  const solves = useMemo(() => solvesMap(stats), [stats])

  const list = useMemo(() => {
    return archiveMode === GAME_MODE_DAILY ? DAILY_LIST: MINI_DAILY_LIST
  }, [archiveMode]);
  const [startDate, setStartDate] = useState<Date|null>(null);
  const startIndex = useMemo(() => {
    if(!startDate) {
      return 0
    }
    return list.getIndex(startDate)
  }, [startDate, list])

  const setDate = useCallback((date: Date) => {
    if(!date) {
      setStartDate(null);
      return
    }
    date.setHours(0, 0, 0, 0);
    setStartDate(date)

    if(listRef.current) {
      listRef.current.scrollToItem(list.getIndex(date), "start");
    }
  }, [list, listRef])

  useEffect(() => {
    if(!isOpen) {
      return
    }
    if(index === null) {
      setDate(getYesterday())
      return
    }
    setDate(list.getDayOfWord(index))
  }, [index, list, listRef, isOpen, setDate]);

  const modeSlug = useMemo(() => {
    return archiveMode === GAME_MODE_DAILY ? 'daily' : 'mini'
  }, [archiveMode])


  const todayIndex = list.getIndex(getToday())
  const archiveRow = ({index, style}: any) => {
    if(index >= todayIndex) {
      return <div style={style}></div> // blank padding rows so we can scroll straight to any puzzle
    }
    const locale = Intl.DateTimeFormat().resolvedOptions().locale
    const sol = list.getSolution(index)
    const day = list.getDayOfWord(index).toLocaleDateString(locale, {
      weekday:"long",
      year:"numeric",
      month:"short",
      day:"numeric"})
    const solve = solves.get(`${archiveMode},${sol.word}`)
    return <div style={style}>
      <Link key={index} to={`/${modeSlug}/${index}`} onClick={handleClose} className="m-1">
        <div className="relative flex flex-col rounded-md p-1 hover:bg-indigo-100 dark:hover:bg-indigo-800 dark:bg-gray-700 bg-gray-200 m-4">
          <div className="flex-col justify-center">
            <p className="text-black dark:text-white font-bold mr-2 text-xl">#{index}</p>
            <p className="text-gray-700 dark:text-gray-300 text-sm mb-2">{day}</p>
          </div>
          <div className="absolute right-2 top-2 h-8 w-8 flex justify-center items-center">
            {solve && <img className={`${solve.guesses === 1 ? 'w-8' : 'w-6'}`} src={numToImage(solve.guesses)} alt={`${solve.guesses} guesses`} />}
          </div>
          <Puzzle puzzle={sol.puzzle} isDarkMode={isDarkMode} size={5} containerSize={8} />
        </div>
      </Link>
    </div>
  }

  const DateField = forwardRef(({ value, onClick }: any, ref: any) => {
    return <div className="flex items-center justify-center"><button className="text-center rounded-md border border-transparent shadow-sm bg-indigo-600 text-base font-medium text-white hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 sm:text-sm" onClick={onClick} ref={ref}>
   <CalendarIcon className="h-6 w-6 m-2 scale-up cursor-pointer dark:stroke-white"
    aria-label="calendar-picker"
    role="button"
 /></button>
    </div>
  });
  const firstDay = list.getDayOfWord(0);
  const lastDay = list.getDayOfWord(todayIndex - 1);
  const isPuzzleDay = (day: Date) => {
    return day.getTime() <= lastDay.getTime() && day.getTime() >= firstDay.getTime();
  }
  const renderDayContents = (day: any, date: Date) => {
    const index = list.getIndex(date);
    const sol = list.getSolution(index)
    const solve = solves.get(`${archiveMode},${sol.word}`)
    const tooltipText = `#${index}`;
    return <div className="inline-flex flex-col" title={tooltipText}>
      <div className="text-md text-xs">{day}</div>
      {isPuzzleDay(date) && <>
      <div className="h-0.5 p-1 flex justify-center items-center">{solve?.guesses &&  <div>&bull;</div>} </div>
      <div className="mb-1 text-[.75em] text-slate-400 leading-none">{tooltipText}</div>
      </>}
    </div>;
  };

  return (
      <BaseModal title="Archive" isOpen={isOpen} handleClose={handleClose}>
        <DatePicker
          filterDate={isPuzzleDay}
          customInput={<DateField />}
          selected={startDate}
          renderDayContents={renderDayContents}
          onChange={setDate} />
        <div className="flex justify-center items-center">
          <ButtonToggle
            selected={archiveMode}
            flagValues={[GAME_MODE_DAILY, GAME_MODE_MINI_DAILY]}
            flagLabels={['Daily', 'Mini']}
            handleFlag={setArchiveMode}
            id="archive-mode"
            />
        </div>
        <List
          ref={(node) => {
            listRef.current = node;
            if(node) {
              node.scrollToItem(startIndex, "start");
            }
          }}
          height={500}
          itemCount={todayIndex + 4} // extra rows so we can scroll
          itemSize={110}
          width={isSm ? 350 : 300}>
          {archiveRow}
        </List>
        <div className="pt-8">
        <p className="text-xs text-indigo-700 dark:text-indigo-400"> Note: We began tracking individual solve stats on 4/13/2023 </p>
        <a className="text-xs text-indigo-700 dark:text-indigo-400" href='https://dryicons.com/icon/bullseye-icon-6275'> Icon by Dryicons </a>
        </div>
      </BaseModal>
		)
}
